import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/stores/useAuthStore'

/**
 * Well inside SESSION_IDLE_TTL_MS, so a dropped beat or two never costs the user
 * their session, and slow enough that an open tab is nearly free.
 */
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000

/** Floor between beats, so a flapping tab cannot become a request per flip. */
const MIN_BEAT_GAP_MS = 10_000

/**
 * Keeps the idle window open while the tab is on screen. Mounted once by
 * `AppShell`.
 *
 * Beating only while visible is the whole point: a tab left open on a forgotten
 * monitor would otherwise hold a session — and its sites and images — alive
 * indefinitely, while a user who tabbed away for an hour and came back mid-review
 * would find themselves signed out. Hidden pauses, visible beats immediately.
 */
export default function SessionHeartbeat(): null {
  const authed = useAuthStore((state) => state.user !== null)
  const heartbeat = useAuthStore((state) => state.heartbeat)
  // Outside the effect, so a remount (StrictMode's double mount included) does not
  // reset the throttle and fire a second beat.
  const lastBeatAt = useRef(0)

  useEffect(() => {
    if (!authed) return

    let timer: number | null = null

    const beat = (): void => {
      const now = Date.now()
      if (now - lastBeatAt.current < MIN_BEAT_GAP_MS) return
      lastBeatAt.current = now
      void heartbeat()
    }

    const stop = (): void => {
      if (timer === null) return
      window.clearInterval(timer)
      timer = null
    }

    const start = (): void => {
      if (timer !== null) return
      timer = window.setInterval(beat, HEARTBEAT_INTERVAL_MS)
    }

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        stop()
        return
      }
      // Beat before the interval would, so a returning user's idle window is
      // refreshed the moment they are back rather than up to two minutes later.
      beat()
      start()
    }

    // Mounting is itself activity: the user just loaded or reloaded the app.
    if (document.visibilityState === 'visible') {
      beat()
      start()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      stop()
    }
  }, [authed, heartbeat])

  return null
}

export { SessionHeartbeat }
